import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { TypeaheadService } from '@app/services/typeahead.service';
import { Subject, takeUntil, mergeMap, switchMap, groupBy, debounceTime, catchError, EMPTY, from } from 'rxjs';

@GridModalComponent('materialitempick')
@Component({
    selector: 'tt-material-item-pick',
    templateUrl: './material-item-pick.component.html',
    styleUrls: ['./material-item-pick.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class MaterialItemPickComponent implements OnChanges, OnDestroy {
    @Input()
    public params?: GetMaterialItemPickParams;

    public get?: MaterialItemPickGet;

    public change$ = new Subject<UpdateMaterialItemPickParams>();

    public destroy$ = new Subject<void>();

    public saving = false;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) context: StateDialogContext,
    ) {
        this.initFromDialog(context);

        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.updateMaterialItemPick(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateMaterialItemPickFromResponse });
    }

    private onUpdateMaterialItemPickFromResponse = (response: Partial<MaterialItemPickGet>) => {
        if (!this.get) return;
        console.log('response :>> ', response);
        for (let k in response) {
            let key = k as keyof MaterialItemPickGet;

            this.get[key] = response[key] as any;
        }
    };

    public openChargeModal() {
        this.modal.openStateDialog({ state: 'charge', params: '' + this.get?.charge_keyno });
    }

    public async openMaterialItemModal() {
        if (!this.get) return;

        const response = await this.modal.openStateDialog({ state: 'materialitem', params: `${this.get.stockbatch_keyno}/0` });

        if (!!response) this.init();
    }

    public async save() {
        if (!this.get) return;

        this.saving = true;

        try {
            const { errorcode, errormessage } = await this.saveMaterialItemPick(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public onChange(key: keyof MaterialItemPickGet) {
        if (!this.get) return;

        this.change$.next({ colname_changed: key, ...this.get });
    }

    private async init() {
        await this.loadGet();
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getMaterialItemPick(this.params);
        }
    }

    private async saveMaterialItemPick(params: MaterialItemPickGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3968, params))[0];
    }

    private async getMaterialItemPick(params: GetMaterialItemPickParams): Promise<MaterialItemPickGet> {
        return (await this.datatask.Post(3966, params))[0];
    }

    private async updateMaterialItemPick(params: UpdateMaterialItemPickParams): Promise<Partial<MaterialItemPickGet>> {
        return (await this.datatask.Post(3967, params))[0];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.change$.complete();
    }

    private async initFromDialog(context?: StateDialogContext) {
        if (!!context) {
            this.params = context.params as unknown as GetMaterialItemPickParams;
            context.setSize({ width: '95vw' });
            await this.init();
            context.setHeading(this.get?.webpage_heading ?? '');
            this.submit.subscribe({ next: (response: ErrorMessageResponse) => context.close(response) });
        }
    }
}

export interface GetMaterialItemPickParams {
    stockbatch_keyno: string;
    argtype: string;
    argvalue: string;
}

export interface MaterialItemPickGet {
    stockbatch_keyno: string;
    suppliers_batchno: string;
    charge_keyno: string;
    charge_id: string;
    prod_id: string;
    prod_name: string;
    density_kg_pr_dm3: string;
    order_internal_no: string;
    orderline_keyno: string;
    stock_keyno: string;
    nbr_of_items: string | number;
    height_mm: string | number;
    width_mm: string | number;
    length_mm: string | number;
    outer_diameter_mm: string | number;
    thickness_mm: string | number;
    volume_dm3: string | number;
    weight_kg: string | number;
    usage_kg: string | number;
    nbr_of_items_rest_full: string | number;
    height_mm_rest_full: string | number;
    width_mm_rest_full: string | number;
    length_mm_rest_full: string | number;
    outer_diameter_mm_rest_full: string | number;
    thickness_mm_rest_full: string | number;
    volume_dm3_rest_full: string | number;
    weight_kg_rest_full: string | number;
    usage_kg_rest_full: string | number;
    nbr_of_items_rest_part: string | number;
    height_mm_rest_part: string | number;
    width_mm_rest_part: string | number;
    length_mm_rest_part: string | number;
    outer_diameter_mm_rest_part: string | number;
    thickness_mm_rest_part: string | number;
    volume_dm3_rest_part: string | number;
    weight_kg_rest_part: string | number;
    usage_kg_rest_part: string | number;
    consumption_kg_total: string | number;
    consumption_items_total: string | number;
    webpage_heading: string;
}

export interface UpdateMaterialItemPickParams extends MaterialItemPickGet {
    colname_changed: keyof MaterialItemPickGet;
}
